<template>
  <component
    :is="tagName"
    :class="currentClass"
    :to="to"
  >
    <slot v-if="noBody" />
    <template v-else>
      <div
        v-if="hasHeaderSlot"
        ref="header"
        class="w-full"
        :class="[headerClass, expandable ? expandableClass : '' ]"
        @click="expandable ? toggle() : null"
      >
        <slot name="header" />
        <o-icon
          v-if="expandable"
          icon="chevronDown"
          :size="12"
          :class="{ 'transform rotate-180' : visible }"
          class="m-0 cursor-pointer transition duration-200 ease-in-out"
        />
      </div>
      <div
        v-else-if="header"
        ref="header"
        :class="[ expandable ? [ headerClass, expandableClass] : headerClass ]"
        @click="expandable ? toggle() : null"
      >
        <div>
          <h2 :class="headerLabelClass">
            {{ header }}
          </h2>
          <p v-if="info" class="text-sm opacity-80 font-normal">
            {{ info }}
          </p>
        </div>
        <div>
          <slot name="control" />
          <o-icon
            v-if="expandable"
            icon="chevronDown"
            :size="12"
            :class="{ 'transform rotate-180' : visible }"
            class="m-0 cursor-pointer transition duration-200 ease-in-out"
          />
        </div>
      </div>
      <div
        v-if="visible"
        ref="body"
        :class="bodyClass"
      >
        <slot />
      </div>
      <div
        v-if="hasFooterSlot"
        ref="footer"
        :class="footerClass"
      >
        <slot name="footer" />
      </div>
    </template>
  </component>
</template>

<script lang="ts">
export default { name: 'OCard' }
</script>

<script setup lang="ts">
interface Props {
  tagName?: string
  to?: string | null
  header?: string | null
  info?: string | null
  expandable?: boolean
  collapsed?: boolean
  actionable?: boolean
  flat?: boolean
  noBody?: boolean
  headerClass?: string
  headerLabelClass?: string
  baseClass?: string
  bodyClass?: string
  flatClass?: string
  footerClass?: string
}

const props = withDefaults(defineProps<Props>(), {
  tagName: 'div',
  to: null,
  header: null,
  info: null,
  expandable: false,
  collapsed: false,
  actionable: false,
  flat: false,
  noBody: false,
  headerClass: 'flex justify-between items-center px-4 pt-3 pb-2 border-b border-gray-100 dark:border-gray-900',
  headerLabelClass: 'font-medium',
  baseClass: 'bg-white dark:bg-gray-800/40 rounded-lg border overflow-hidden',
  bodyClass: 'p-5',
  flatClass: 'bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-800 shadow-none overflow-hidden',
  footerClass: 'px-5 py-3 border-t',
})

const visible = ref(!props.expandable)
const expandableClass = 'hover:bg-green-100 cursor-pointer transition-all duration-500 ease-in-out cursor-pointer'
const actionableClass = 'transition ease-in-out duration-300 transform hover:-translate-y-1 hover:shadow-md'

const slots = useSlots()

const hasHeaderSlot = computed(() => !!slots.header)
const hasFooterSlot = computed(() => !!slots.footer)

const currentClass = computed(() => {
  const classes = ['o-card']
  if (props.flat) {
    classes.push(props.flatClass)
  } else {
    classes.push(props.baseClass)
  }
  if (props.actionable) {
    classes.push(actionableClass)
  }
  return classes
})

onMounted(() => {
  visible.value = !props.collapsed
})

function toggle() {
  visible.value = !visible.value
}
</script>
